/**
 * The YAGNI Review App, made visible where it matters. On an Autonomous
 * review line Proctor's approval comes from that second GitHub App, and it
 * counts toward branch protection only when the App is installed on the
 * repository with Contents: write accepted. Without it the pull request stays
 * "review required" and the Team waits on a person with nothing saying why.
 *
 * This reads `GET /api/code-review/review-app` and, when a repository is
 * missing the App or the permission, says so in one line with the install
 * link. It informs; nothing waits on it, and a failed read says nothing.
 */
import { useEffect, useState } from "react";
import type { ReviewAppInstallResponse, ReviewAppRepo } from "@yagni/shared";
import { captureUiError } from "../sentry.js";
import { REVIEW_APP_REASON } from "../work/workCopy.js";

/** A read is reused for this long, so opening a sheet twice asks GitHub once. */
const CACHE_MS = 5 * 60 * 1000;
const cache = new Map<string, { at: number; body: ReviewAppInstallResponse }>();

/** Reads in flight, so two notes on one page share one request. */
const inflight = new Map<string, Promise<ReviewAppInstallResponse | null>>();

/** Forget every cached read (for tests). */
export function clearReviewAppInstallCache(): void {
  cache.clear();
  inflight.clear();
}

function readOnce(key: string, query: string): Promise<ReviewAppInstallResponse | null> {
  const pending = inflight.get(key);
  if (pending) return pending;
  const read = fetch(`/api/code-review/review-app${query}`, { credentials: "include" })
    .then((res) => {
      if (res.ok) return res.json() as Promise<ReviewAppInstallResponse>;
      // The note stays silent, but a broken check is reported, never mistaken for "all installed".
      captureUiError(new Error(`review App check answered ${res.status}`), {
        area: "Proctor",
        operation: "read_review_app_install",
        severity: "warning",
      });
      return null;
    })
    .then((next) => {
      const body = next && Array.isArray(next.repos) ? next : null;
      if (body) cache.set(key, { at: Date.now(), body });
      return body;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, read);
  return read;
}

function keyOf(repos: readonly string[] | null): string {
  return repos === null
    ? "*"
    : [...repos]
        .map((repo) => repo.toLowerCase())
        .sort()
        .join(",");
}

/**
 * The review App's state on `repos`, or on every repository an Autonomous
 * review line reviews when `repos` is null. Null until read, when disabled,
 * or when the read failed.
 */
export function useReviewAppInstall(
  repos: readonly string[] | null,
  enabled: boolean,
): ReviewAppInstallResponse | null {
  const key = keyOf(repos);
  const [body, setBody] = useState<ReviewAppInstallResponse | null>(() => {
    const hit = cache.get(key);
    return hit && Date.now() - hit.at < CACHE_MS ? hit.body : null;
  });

  useEffect(() => {
    if (!enabled || (repos !== null && repos.length === 0)) {
      setBody(null);
      return;
    }
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < CACHE_MS) {
      setBody(hit.body);
      return;
    }
    let cancelled = false;
    readOnce(key, repos === null ? "" : `?repos=${encodeURIComponent(key)}`)
      .then((next) => {
        if (!cancelled) setBody(next);
      })
      .catch((err: unknown) => {
        captureUiError(err, {
          area: "Proctor",
          operation: "read_review_app_install",
          severity: "warning",
        });
        if (!cancelled) setBody(null);
      });
    return () => {
      cancelled = true;
    };
    // `key` stands for `repos`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return enabled ? body : null;
}

/** The repositories where Proctor's approval would not count yet. */
export function missingReviewApp(body: ReviewAppInstallResponse | null): ReviewAppRepo[] {
  if (!body?.configured) return [];
  return body.repos.filter(
    (repo) => repo.state === "not_installed" || repo.state === "no_contents_write",
  );
}

/** "Not installed on acme/web." / "Installed without Contents: write on acme/api." */
export function missingSentence(missing: readonly ReviewAppRepo[]): string {
  const names = (state: ReviewAppRepo["state"]) =>
    missing.filter((repo) => repo.state === state).map((repo) => repo.repo);
  const notInstalled = names("not_installed");
  const noWrite = names("no_contents_write");
  return [
    notInstalled.length > 0 ? `Not installed on ${notInstalled.join(", ")}.` : null,
    noWrite.length > 0 ? `Installed without Contents: write on ${noWrite.join(", ")}.` : null,
  ]
    .filter(Boolean)
    .join(" ");
}

export function ReviewAppInstallNote({
  repos,
  enabled = true,
  variant = "qnote",
  testId = "review-app-install-note",
}: {
  /** The repositories to ask about; null asks about every Autonomous review line's. */
  repos: readonly string[] | null;
  enabled?: boolean;
  /** `qnote` for a page notice, `help` for a line inside a settings field. */
  variant?: "qnote" | "help";
  testId?: string;
}) {
  const body = useReviewAppInstall(repos, enabled);
  const missing = missingReviewApp(body);
  if (missing.length === 0) return null;
  const link = body?.installUrl ? (
    <a
      href={body.installUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`${testId}-link`}
    >
      Install YAGNI Review
    </a>
  ) : null;
  if (variant === "help") {
    return (
      <div className="help" data-testid={testId}>
        {REVIEW_APP_REASON} {missingSentence(missing)} {link}
      </div>
    );
  }
  return (
    <div className="qnote info" data-testid={testId}>
      <span>
        {REVIEW_APP_REASON} {missingSentence(missing)}
      </span>
      {link && <span className="acts">{link}</span>}
    </div>
  );
}
